import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
  NormalizedCacheObject,
  TypePolicies,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { emitError } from './common/components/notification/notification-bus';

// Mutations that own their own error UX (e.g., BoardPage's optimistic-drag
// rollback Snackbar). Errors from these are NOT surfaced via the global
// notification host to avoid duplicate Snackbars.
export const OPS_WITH_LOCAL_ERROR_HANDLING = new Set([
  'BoardCardMove',
  'BoardColumnMove',
  // Comment ops render inline errors (dialog Alert / composer / per-action).
  'getComments',
  'commentCreate',
  'commentUpdate',
  'commentDelete',
  'commentResolve',
  'commentReopen',
]);

// Called by the error link when an auth error is detected.
// Clears local auth state and redirects to /login without a full page reload.
function handleAuthError() {
  if (
    typeof window !== 'undefined' &&
    (window as { Cypress?: unknown }).Cypress
  ) {
    return;
  }

  localStorage.removeItem('token');
  localStorage.removeItem('user');
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
}

// Shared cache config for every client created here (also imported by tests).
export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      projectVersion: {
        // Create unique cache keys for different project versions
        keyArgs: ['projectId', 'version'],
      },
    },
  },
  ProjectType: {
    // Use projectId and version for cache key to prevent conflicts
    keyFields: false, // Disable default cache key to allow custom logic
  },
};

// Reads required GraphQL URL from env and fails fast if missing.
const getGraphQLUrl = () => {
  const envUrl = import.meta.env.VITE_GRAPHQL_URL;
  if (!envUrl) {
    throw new Error(
      'GraphQL URL is not defined! Please set VITE_GRAPHQL_URL environment variable.'
    );
  }

  return envUrl;
};

// Create Apollo Client factory function
const createApolloClient = () => {
  const httpLink = createHttpLink({
    uri: getGraphQLUrl(),
  });

  const authLink = setContext(async (_, { headers }) => {
    // Get token from localStorage
    const token = localStorage.getItem('token');

    return {
      headers: {
        ...headers,
        // Add client identifier for debugging
        'x-client': 'vite-apollo',
        // Add authorization header if token exists
        ...(token && token !== 'nextauth'
          ? { Authorization: `Bearer ${token}` }
          : {}),
      },
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    // Auth errors always route through the redirect, regardless of who
    // owns local error UX.
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        const code = err.extensions?.code as string | undefined;
        const message = err.message ?? '';
        if (
          code === 'UNAUTHENTICATED' ||
          message.toLowerCase().includes('invalid or expired token') ||
          message.toLowerCase().includes('not authenticated')
        ) {
          handleAuthError();
          return;
        }
      }
    }

    // Callers that own their error UX (drag rollback, etc.) suppress the
    // global toast for both graphQLErrors AND networkError — the earlier
    // split emitted two Snackbars when a drag mutation failed on the wire.
    if (OPS_WITH_LOCAL_ERROR_HANDLING.has(operation.operationName)) return;

    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        emitError(err.message ?? 'Something went wrong.');
      }
    } else if (networkError) {
      emitError('Network error — please try again.');
    }
  });

  const apolloClient = new ApolloClient({
    link: errorLink.concat(authLink).concat(httpLink),
    cache: new InMemoryCache({
      typePolicies,
    }),
    ssrMode: typeof window === 'undefined',
  });

  if (
    typeof window !== 'undefined' &&
    (window as { Cypress?: unknown }).Cypress
  ) {
    (
      window as typeof window & {
        __APOLLO_CLIENT__: ApolloClient<NormalizedCacheObject>;
      }
    ).__APOLLO_CLIENT__ = apolloClient;
  }

  return apolloClient;
};

// Create a simple Apollo Client without auth headers (for unauthenticated requests)
const createSimpleApolloClient = () => {
  const httpLink = createHttpLink({
    uri: getGraphQLUrl(),
  });

  return new ApolloClient({
    link: httpLink,
    cache: new InMemoryCache({
      typePolicies,
    }),
    ssrMode: typeof window === 'undefined',
  });
};

// Create client instance for server-side and fallback
let clientInstance: ApolloClient<NormalizedCacheObject> | null = null;
let simpleClientInstance: ApolloClient<NormalizedCacheObject> | null = null;

export const getClient = () => {
  if (!clientInstance) {
    clientInstance = createApolloClient();
  }
  return clientInstance;
};

// Get simple client for unauthenticated requests (no auth required)
export const getSimpleClient = () => {
  if (!simpleClientInstance) {
    simpleClientInstance = createSimpleApolloClient();
  }
  return simpleClientInstance;
};

// Export the client for backward compatibility
export const client = getClient();

export { ApolloProvider };
